import { Grid, Typography, Button, CircularProgress } from "@material-ui/core";
import { CheckCircle, Cancel, Assessment, Warning } from "@material-ui/icons";
import { useState, useEffect, useCallback } from "react";

import SimpleModal from "../../shared/SimpleModal";
import { trackPromise } from 'react-promise-tracker';
import memberService from "../../../domain/services/solicitation/member.service";

const IA_REDO_VALID_PERCENTAGE = 80;

const RequestRedoAnalysisDialog = ({ request = {}, analysis = {}, isOpen, onClose, setAlert }) => {
    
    const fadeInStyle = `
        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(-10px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
    `;

    const [analysisData, setAnalysisData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasAnalysisData, setHasAnalysisData] = useState(true);
    const [apiError, setApiError] = useState(false);
    const [lastRequestId, setLastRequestId] = useState(null);

    const fetchAnalysisData = useCallback(async (requestId) => {
        
        if (!requestId)
            return;
        
        setLoading(true);
        setApiError(false);
        setHasAnalysisData(true);
        
        try {
            const response = await trackPromise(
                memberService.find(requestId)
            );

            const members = response?.data || [];

            const faciliterMember = members.find(member => member.serviceRating);
 
            if (faciliterMember?.serviceRating?.redoAnalysisDetails) {
                const serviceRating = faciliterMember.serviceRating;
                const analysisDetails = serviceRating.redoAnalysisDetails;
                
                const mappedData = {
                    redoPercentage: analysisDetails?.redoPercentage,
                    isValid: serviceRating?.redoAnalysisStatus === 'approved',
                    additionalValue: serviceRating?.redoAnalysisStatus === 'approved' && serviceRating?.additionalValue ? serviceRating?.additionalValue : 0,
                    analysisDetails: analysisDetails?.details || '',
                    faciliterDetails: serviceRating?.faciliterDetails,
                    analysisDate: serviceRating.redoAnalyzedAt,
                    authorUserId: serviceRating?.authorUserId ?? null,
                    reviewerRedo: serviceRating?.reviewerRedo === true
                };
                
                setAnalysisData(mappedData);
                setHasAnalysisData(true);
            } else if (faciliterMember?.serviceRating) {
                setHasAnalysisData(false);
                setAnalysisData(null);
            } else {
                setHasAnalysisData(false);
                setAnalysisData(null);
            }
        } catch (error) {
            setApiError(true);
            setHasAnalysisData(false);
            setAnalysisData(null);
            if (setAlert) {
                setAlert('Erro ao carregar dados da análise. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    useEffect(() => {
        if (isOpen && request?.id) {
            if (lastRequestId === request.id && analysisData) {
                return;
            }
            
            if (analysis?.redoPercentage) {
                setAnalysisData(analysis);
                setHasAnalysisData(true);
                setApiError(false);
                setLastRequestId(request.id);
            } else {
                if (!loading) {
                    setLastRequestId(request.id);
                    fetchAnalysisData(request.id);
                }
            }
        }
        
        if (!isOpen) {
            setAnalysisData(null);
            setLoading(false);
            setHasAnalysisData(true);
            setApiError(false);
            setLastRequestId(null);
        }
    }, [isOpen, request?.id]);

    const NoDataMessage = ({ isError }) => (
        <div 
            className="mb-6 p-6 border-l-4 rounded-r-lg text-center"
            style={{ 
                borderColor: isError ? '#dc2626' : '#f59e0b',
                backgroundColor: isError ? '#fef2f2' : '#fffbeb',
                animation: 'fadeIn 0.3s ease-in-out'
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <Warning 
                    style={{ 
                        color: isError ? '#dc2626' : '#f59e0b', 
                        fontSize: '3rem'
                    }} 
                />
            </div>
            <Typography 
                variant="h6"
                style={{
                    fontWeight: 600,
                    color: isError ? '#dc2626' : '#f59e0b',
                    fontSize: '1.3rem',
                    marginBottom: '12px'
                }}
            >
                {isError ? 'Erro ao Carregar Dados' : 'Análise Não Disponível'}
            </Typography>
            <Typography 
                variant="body1"
                style={{ 
                    color: isError ? '#991b1b' : '#92400e',
                    fontSize: '1rem',
                    lineHeight: 1.5,
                    marginBottom: '16px'
                }}
            >
                {isError 
                    ? 'Ocorreu um erro ao tentar carregar os dados da análise. Verifique sua conexão e tente novamente.'
                    : 'Não há análise de refazimento para esta solicitação.'
                }
            </Typography>
            <Button
                variant="outlined"
                onClick={onClose}
                style={{
                    color: isError ? '#dc2626' : '#f59e0b',
                    borderColor: isError ? '#dc2626' : '#f59e0b',
                    padding: '8px 24px',
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 500
                }}
            >
                Fechar
            </Button>
        </div>
    );

    if (loading) {
        return (
            <>
                <style>{fadeInStyle}</style>
                <SimpleModal
                    open={isOpen}
                    onClose={onClose}
                    maxWidth="md"
                    bodyStyle={{ padding: 48, borderRadius: 12 }}
                >
                    <div className="text-center">
                        <Typography 
                            variant="h4" 
                            component="h2"
                            style={{ 
                                fontWeight: 500, 
                                color: '#1a1a1a',
                                marginBottom: 40,
                                fontSize: '1.75rem'
                            }}
                        >
                            Carregando Análise...
                        </Typography>
                        <CircularProgress size={50} />
                        <Typography variant="body1" style={{ marginTop: '24px', color: '#6b7280' }}>
                            Buscando dados da análise do refazimento...
                        </Typography>
                    </div>
                </SimpleModal>
            </>
        );
    }

    if (!hasAnalysisData || apiError) {
        return (
            <>
                <style>{fadeInStyle}</style>
                <SimpleModal
                    open={isOpen}
                    onClose={onClose}
                    maxWidth="md"
                    bodyStyle={{ padding: 48, borderRadius: 12 }}
                >
                    <div className="text-center mb-8">
                        <Typography 
                            variant="h4" 
                            component="h2"
                            style={{ 
                                fontWeight: 500, 
                                color: '#1a1a1a',
                                lineHeight: 1.3,
                                marginBottom: 20,
                                fontSize: '1.75rem'
                            }}
                        >
                            Análise do Refazimento da Petição
                        </Typography>
                        <div 
                            style={{ 
                                width: 60, 
                                height: 4, 
                                backgroundColor: '#1976d2', 
                                margin: '0 auto',
                                borderRadius: 2
                            }} 
                        />
                    </div>
                    <NoDataMessage isError={apiError} />
                </SimpleModal>
            </>
        );
    }

    const displayData = analysisData;

    const isReviewerRedo = displayData?.reviewerRedo === true || !!displayData?.authorUserId;
    const isPositiveOutcome = displayData?.isValid && !isReviewerRedo;
    const statusLabel = displayData?.isValid
        ? (isReviewerRedo ? 'Penalização aplicada ao redator' : 'Válido')
        : (isReviewerRedo ? 'Sem penalização' : 'Inválido');
    const emptyEffectNote = isReviewerRedo
        ? 'Refazimento abaixo do limite, sem penalização ao redator.'
        : 'O valor pago permanece o mesmo, sem adicional de refazimento.';
    const percentageColor = isReviewerRedo
        ? (displayData?.isValid ? '#dc2626' : '#f59e0b')
        : (displayData?.redoPercentage >= IA_REDO_VALID_PERCENTAGE ? '#059669' : '#dc2626');

    const StatusIcon = () => {
        if (isPositiveOutcome)
            return <CheckCircle style={{ color: '#059669', fontSize: '1.5rem' }} />;
        return <Cancel style={{ color: '#f59e0b', fontSize: '1.5rem' }} />;
    };

    return (
        <>
            <style>{fadeInStyle}</style>
            <SimpleModal
                open={isOpen}
                onClose={onClose}
                maxWidth="md"
                bodyStyle={{ padding: 48, borderRadius: 12 }}
            >
                <div className="text-center mb-8">
                    <Typography 
                        variant="h4" 
                        component="h2"
                        style={{ 
                            fontWeight: 500, 
                            color: '#1a1a1a',
                            lineHeight: 1.3,
                            marginBottom: 20,
                            fontSize: '1.75rem'
                        }}
                    >
                        Análise do Refazimento da Petição
                    </Typography>
                    <div 
                        style={{ 
                            width: 60, 
                            height: 4, 
                            backgroundColor: '#1976d2', 
                            margin: '0 auto',
                            borderRadius: 2
                        }} 
                    />
                </div>

                <Grid className="text-base">
                    
                    {/* Status do Refazimento */}
                    <div 
                        className="mb-6 p-4 border-l-4 rounded-r-lg"
                        style={{ 
                            borderColor: isPositiveOutcome ? '#059669' : '#f59e0b',
                            backgroundColor: isPositiveOutcome ? '#f0fdf4' : '#fffbeb',
                            animation: 'fadeIn 0.3s ease-in-out',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            minHeight: '60px'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography 
                                variant="body1"
                                style={{
                                    color: isPositiveOutcome ? '#059669' : '#f59e0b',
                                    fontSize: '1.3rem',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <StatusIcon />
                                {statusLabel}
                            </Typography>
                            {displayData?.isValid && !!displayData?.additionalValue && (
                                <Typography
                                    variant="body1"
                                    style={{
                                    color: isReviewerRedo ? '#dc2626' : '#059669',
                                        fontSize: '1.1rem',
                                        fontWeight: 600
                                    }}
                                >
                                    {isReviewerRedo ? '- ' : '+ '}R$ {Math.abs(displayData?.additionalValue).toFixed(2).replace('.', ',')}
                                </Typography>
                            )}
                        </div>
                        {!displayData?.isValid && (
                            <Typography
                                variant="body2"
                                 style={{
                                    color: '#f59e0b',
                                    fontSize: '0.95rem',
                                    marginTop: '8px'
                                }}
                            >
                                {emptyEffectNote}
                            </Typography>
                        )}
                    </div>

                    {/* Seção do Percentual */}
                    <div 
                        className="mb-4 p-3 border-l-4 rounded-r-lg text-center"
                        style={{ 
                            borderColor: '#3b82f6',
                            backgroundColor: '#f8faff',
                            animation: 'fadeIn 0.3s ease-in-out'
                        }}
                    >
                        <Typography 
                            variant="h6"
                            style={{
                                fontWeight: 600,
                                color: '#1976d2',
                                fontSize: '0.95rem',
                                marginBottom: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px'
                            }}
                        >
                            <Assessment style={{ fontSize: '1.1rem' }} /> Percentual de Refazimento
                        </Typography>
                        {loading ? (
                            <div style={{ margin: '12px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" style={{ marginTop: '6px', color: '#6b7280', fontSize: '0.8rem' }}>
                                    Carregando análise...
                                </Typography>
                            </div>
                        ) : (
                            <>
                                <Typography 
                                    style={{
                                        fontSize: '1.8rem',
                                        fontWeight: 700,
                                        color: percentageColor,
                                        margin: '4px 0 6px 0'
                                    }}
                                >
                                    {displayData?.redoPercentage}%
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    style={{ 
                                        color: '#6b7280',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    Análise realizada em {new Date(displayData?.analysisDate).toLocaleDateString('pt-BR')}
                                </Typography>
                            </>
                        )}
                    </div>

                    {/* Detalhes do Faciliter */}
                    {(displayData?.faciliterDetails || '').length > 0 ? (
                    <div
                        className="mb-6 p-4 border-l-4 rounded-r-lg"
                        style={{ 
                            borderColor: '#6366f1',
                            backgroundColor: '#f8faff',
                            animation: 'fadeIn 0.3s ease-in-out'
                        }}
                    >
                        <div>
                            <Typography 
                                variant="h6"
                                style={{
                                    fontWeight: 600,
                                    color: '#4338ca',
                                    fontSize: '1.2rem',
                                    marginBottom: '16px'
                                }}
                            >
                                Detalhes Fornecidos pelo Faciliter
                            </Typography>
                            <Typography 
                                variant="body1"
                                style={{ 
                                    color: '#374151',
                                    fontSize: '1rem',
                                    lineHeight: 1.6,
                                    fontStyle: 'italic',
                                    backgroundColor: '#ffffff',
                                    padding: '16px',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb'
                                }}
                            >
                                "{displayData?.faciliterDetails}"
                            </Typography>
                        </div>
                    </div>
                    ) : <></>}

                    {/* Detalhes da Análise */}
                    <div 
                        className="mb-6 p-4 border-l-4 rounded-r-lg"
                        style={{ 
                            borderColor: '#f97316',
                            backgroundColor: '#fff7ed',
                            animation: 'fadeIn 0.3s ease-in-out'
                        }}
                    >
                        <Typography 
                            variant="h6"
                            style={{
                                fontWeight: 600,
                                color: '#ea580c',
                                fontSize: '1.2rem',
                                marginBottom: '16px'
                            }}
                        >
                            Detalhes da Análise
                        </Typography>
                        <div 
                            className="w-full border-2 rounded-md border-orange-300 bg-white"
                            style={{ 
                                fontSize: '0.95rem',
                                fontFamily: 'inherit',
                                backgroundColor: '#ffffff',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                padding: '12px',
                                lineHeight: '1.5',
                                color: '#9a3412',
                                fontStyle: 'italic',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                overflow: 'visible'
                            }}
                        >
                            {displayData?.analysisDetails || 'Nenhum detalhe adicional fornecido pela análise.'}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="text-center" style={{ marginTop: 40 }}>
                        <Button
                            color="primary"
                            variant="contained"
                            size="large"
                            onClick={onClose}
                            style={{
                                padding: '16px 32px',
                                borderRadius: 8,
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                transition: 'all 0.2s ease-in-out'
                            }}
                            className="hover:shadow-lg"
                        >
                            Fechar Análise
                        </Button>
                    </div>
                </Grid>

            </SimpleModal>
        </>
    )
}

export default RequestRedoAnalysisDialog;
